/**
 * Profiling selection model.
 *
 * selection = { [answerId]: { categoryId, questionId } }
 *
 * The backend stores only answer ids and returns them as `profilingAnswers: [{ id, profiling_answers }]`
 * (answer id + text), so the owning question/category are looked up from the categories data
 * (GET /global_profiling/get_all_categories, which embeds questions and answers).
 */
export const toggleAnswer = (selection, categoryId, questionId, answerId) => {
  const next = { ...selection };
  if (next[answerId]) delete next[answerId];
  else next[answerId] = { categoryId, questionId };
  return next;
};

/** Every answer id in the categories data -> where it lives. */
export function indexAnswers(categories) {
  const index = {};
  (categories || []).forEach((c) => (c.profiling_questions || []).forEach((q) => (q.profiling_answers || []).forEach((a) => {
    index[a.id] = { categoryId: c.id, questionId: q.id };
  })));
  return index;
}

/**
 * Saved `profilingAnswers` -> selection. Saved answers that no longer exist in the categories data are
 * kept (so saving again does not silently drop them) but have no category to display under.
 */
export function selectionFromSaved(profilingAnswers, categories) {
  const index = indexAnswers(categories);
  const selection = {};
  (profilingAnswers || []).forEach((a) => {
    if (a && a.id) selection[a.id] = index[a.id] || { categoryId: null, questionId: null };
  });
  return selection;
}

/** Request body: a plain array of answer ids. */
export const selectionToPayload = (selection) => Object.keys(selection);

export const selectedInCategory = (selection, categoryId) => Object.values(selection).filter((v) => v.categoryId === categoryId).length;

/** True when the selection equals what is saved (order-insensitive). */
export function sameSelection(selection, profilingAnswers) {
  const saved = new Set((profilingAnswers || []).map((a) => a.id));
  const keys = Object.keys(selection);
  return keys.length === saved.size && keys.every((k) => saved.has(k));
}

/**
 * The target group's saved profiling answers, grouped under their question for display (used by the quota pickers).
 * Answers that cannot be found in the categories data (or while it is still loading) go under "Other answers".
 */
export function groupSavedAnswers(profilingAnswers, categories) {
  const questions = {};
  (categories || []).forEach((c) => (c.profiling_questions || []).forEach((q) => { questions[q.id] = q.profiling_questions; }));
  const index = indexAnswers(categories);
  const groups = new Map();
  (profilingAnswers || []).forEach((a) => {
    if (!a || !a.id) return;
    const questionId = index[a.id] ? index[a.id].questionId : null;
    const title = (questionId && questions[questionId]) || 'Other answers';
    if (!groups.has(title)) groups.set(title, []);
    groups.get(title).push({ id: a.id, name: a.profiling_answers || a.id });
  });
  return [...groups].map(([question, answers]) => ({ question, answers }));
}
